import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router/composables";
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import { deletePrivateRate, getPrivateRateSingle, updatePrivateRate } from "@/api/offers";
import { delay, showServerError } from "@/utils";
import { PrivateRate } from "@/api/types/offers";
import BasePageTabs, { ITab } from "@/components-UI/BasePageTabs.vue";
import { updateQueryString } from "@/utils/updateQueryString";
import { useWindowResize } from "@/utils/useWindowSize";
import { storeToRefs } from "pinia";
import { usePrivateRatesStore } from "@/store_v3/PrivateRatesStore";
import BaseStatus from "@/components-UI/BaseStatus.vue";
import { IStatus } from "@/types";
import TrashButton from "@/components-UI/buttons/TrashButton.vue";
import SvgReset from "@/assets/icons/reload.svg";
import SvgEdit from "@/assets/icons/edit-pen-white.svg";
import SvgPyramid from "@/assets/icons/line-pyramid.svg";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import SvgCheck from "@/assets/icons/check.svg";
import BaseModal from "@/components-UI/modals/BaseModal.vue";
import ActionButton from "@/components-UI/buttons/ActionButton.vue";
import SvgTrash from "@/assets/icons/trash-white.svg";
import { USER_ROLES } from "@/mappings/user-roles";
import BaseConfirmModal from "@/components-UI/modals/BaseConfirmModal.vue";
import { hasRole } from "@/utils/checkRole";
import SvgCopy from "@/assets/icons/copy2.svg";
import BaseButton from "@/components-UI/BaseButton.vue";

type TABS = keyof typeof TABS;


export default /*#__PURE__*/_defineComponent({
  __name: 'PrivateRateSingle',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const isLoading = ref(true);
const isEdit = ref(false);
const { isNeedUpdateRate, editableFields } = storeToRefs(usePrivateRatesStore());
const { width } = useWindowResize();
const isMobile = computed(() => width.value < 768);
const isMobileTabsPosition = computed(() => width.value < 1264);
const getButtonContentVariant = computed(() => isMobile.value ? 'iconOnly' : 'withIcon');
const title = computed(() => `Цена ${id! || ''}`);
const rate = ref<PrivateRate | null>(null);
const status = ref<IStatus | null>(null);
let id = '';

onBeforeMount(() => {
    id = route.params.id!;
    getRate();
    updateTabQueryOnLoad();
    document.title = title.value;
});

watchEffect(() => {
    if (isNeedUpdateRate.value) {
        getRate();
        isNeedUpdateRate.value = false;
    }
});

const isDisabledDeleteButton = computed(() => {
    const status = rate.value?.rate_status;
    const isDeletable = status === 'active' || status === 'planned';
    if (isDeletable) {
        const startTime = new Date(rate.value?.created_at).getTime();
        const oneWeekInMilliseconds = 168 * 60 * 60 * 1000;
        const targetTime = startTime + oneWeekInMilliseconds;
        const currentTime = new Date().getTime();
        return currentTime >= targetTime;
    } else {
        return true;
    }
});

const getRate = async () => {
    try {
        isLoading.value = true;
        rate.value = await getPrivateRateSingle(id);
        status.value = getStatus(rate.value.rate_status);
    } catch (err) {
        showServerError(err, 'Цена не загружена');
    } finally {
        isLoading.value = false;
    }
};

const getStatus = (status: string) => {
    switch (status) {
    case 'active':
        return { text: 'Актуальная', color: 'green' };
    case 'inactive':
        return { text: 'Не актуальная', color: 'red' };
    case 'planned':
        return { text: 'Запланирована', color: 'orange' };
    default:
        return { text: status, color: '' };
    }
};

// -- Tabs
const TABS = {
    MAIN: 'main',
    HISTORY: 'history',
} as const;
const tabs: ITab[] = [
    { text: 'Основное', value: TABS.MAIN },
    { text: 'История изменений', value: TABS.HISTORY },
];

const currentTab = ref<TABS>(TABS.MAIN);

const tabComponent = {
    [TABS.MAIN]: () => import('@/components_v3/offers/private-rates/PrivateRateMain.vue'),
    [TABS.HISTORY]: () => import('@/components_v3/offers/private-rates/PrivateRateHistory.vue'),
};

const changeTab = (value: string) => {
    currentTab.value = value;
    updateQueryString({ tab: value });
};

const updateTabQueryOnLoad = () => {
    const tab = route.query.tab || TABS.MAIN;
    updateQueryString({ tab });
    currentTab.value = tab;
};

// -- Edit
const isCanEdit = computed(() => [USER_ROLES.ADMIN, USER_ROLES.ACCOUNT_MANAGER].some(hasRole));

const canMutateRate = computed(() => !hasRole(USER_ROLES.NEW_AFFILIATE_MANAGER));

const isShowEditButtons = computed(() => {
    return currentTab.value === TABS.MAIN;
});

const disableEditMode = () => {
    isEdit.value = false;
};

const enableEditMode = () => {
    isEdit.value = true;
};

const saveRate = async () => {
    try {
        isLoading.value = true;
        disableEditMode();
        await delay(500);
        await updatePrivateRate(id, editableFields.value);
        await getRate();
    } catch (err) {
        showServerError(err, 'Цена не сохранена');
    } finally {
        isLoading.value = false;
    }
};

// --Delete
const isShowDeleteModal = ref(false);

const showDeleteModal = () => {
    isShowDeleteModal.value = true;
};

const hideDeleteModal = () => {
    isShowDeleteModal.value = false;
};

const deleteRate = async () => {
    try {
        isLoading.value = true;
        await deletePrivateRate(id);
        await router.push({ name: 'private-rates' });
    } catch (err) {
        showServerError(err, 'Цена не удалена');
    } finally {
        isLoading.value = false;
    }
};

// --Leave
const confirmModal = ref(null);
onBeforeRouteLeave( async (to, from, next) => {
    if (isEdit.value) {
        const message = 'Изменения не сохранены. Вы действительно хотите покинуть страницу?';
        const isLeave = await confirmModal.value?.openModal(message);
        if (isLeave) {
            next();
        } else {
            next(false);
        }
    } else next();
});

// --Redirect
const redirectToPersonalRates = () => {
    const query = {
        offer_legacy_id: [rate.value.offer_id],
    };
    if (rate.value.affiliate_id) {
        query.affiliate_id = [rate.value.affiliate_id];
    }
    router.push({ name: 'personal-rates', query });
};

return { __sfc: true,route, router, isLoading, isEdit, isNeedUpdateRate, editableFields, width, isMobile, isMobileTabsPosition, getButtonContentVariant, title, rate, status, id, isDisabledDeleteButton, getRate, getStatus, TABS, tabs, currentTab, tabComponent, changeTab, updateTabQueryOnLoad, isCanEdit, canMutateRate, isShowEditButtons, disableEditMode, enableEditMode, saveRate, isShowDeleteModal, showDeleteModal, hideDeleteModal, deleteRate, confirmModal, redirectToPersonalRates, PageLayout, BasePageTabs, BaseStatus, TrashButton, SvgReset, SvgEdit, SvgPyramid, BaseButtonV2, SvgCheck, BaseModal, ActionButton, SvgTrash, BaseConfirmModal, SvgCopy, BaseButton }
}

})